import { Children, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery.js';

// How far past the axis-decision deadzone before we lock to horizontal/vertical.
const AXIS_DEADZONE_PX = 8;
// Snap to the next pane if the drag crosses this fraction of the viewport...
const SNAP_FRACTION = 1 / 3;
// ...OR if it's a quick flick: at least this far, at least this fast (px/ms).
const FLICK_MIN_PX = 40;
const FLICK_MIN_VELOCITY = 0.5;

// Mobile-only horizontal carousel between panes. On md+ it is inert and renders
// its children as a fragment so the parent's flex layout lays them side-by-side.
// On mobile the children sit in a track twice the viewport wide; a horizontal
// finger drag moves the track (drag-follow) and snaps to the nearer pane on
// release. A second finger (pinch) or a non-moving tap never claims the gesture,
// so the Map's pinch-zoom and tap-to-select keep working; vertical drags fall
// through to native scrolling (the Table list) via touch-action: pan-y.
//
// Props:
//   tab      — active pane key (must match one of `tabs`)
//   onChange — called with a pane key when the active pane should change
//   tabs     — ordered array of pane keys, parallel to children order
//   children — exactly `tabs.length` pane elements, in the same order
export default function SwipeView({ tab, onChange, tabs, children }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const panes = Children.toArray(children);
  const count = panes.length;
  const index = Math.max(0, tabs.indexOf(tab));

  // `drag` (px, finger delta) is the only per-move React state; null when idle.
  const [drag, setDrag] = useState(null);
  // The clipping viewport — its clientWidth is one pane wide (the track is
  // `count`× wider, so we must not measure width from the track).
  const viewportRef = useRef(null);
  // The translating track — we attach a non-passive touchmove listener to it.
  const trackRef = useRef(null);
  // Active-gesture bookkeeping kept in a ref to avoid re-render churn.
  // pointers: live pointer ids (size >= 2 ⇒ pinch, abandon swipe)
  const g = useRef({ pointers: new Set(), id: null, startX: 0, startY: 0, axis: null, width: 0, startT: 0 });

  // If we cross into desktop mid-gesture, drop any in-flight drag.
  useEffect(() => {
    if (isDesktop) {
      g.current.pointers.clear();
      g.current.id = null;
      g.current.axis = null;
      setDrag(null);
    }
  }, [isDesktop]);

  // Suppress the browser's native touch handling for horizontal drags. In a
  // `touch-action: pan-y` region the browser otherwise hands the touch to its
  // scroll machinery and fires pointercancel — killing the swipe mid-gesture.
  // A non-passive touchmove listener that preventDefault()s while the move is
  // horizontal keeps the gesture ours, yet still lets vertical drags scroll the
  // Table natively (we don't preventDefault those). React's onTouchMove is
  // passive, so this must be attached imperatively (cf. Map.jsx).
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const onTouchMove = (ev) => {
      if (ev.touches.length >= 2) return; // pinch ⇒ leave it to the map
      const t = ev.touches[0];
      const dx = t.clientX - g.current.startX;
      const dy = t.clientY - g.current.startY;
      const leaningHorizontal = Math.abs(dx) > Math.abs(dy) && Math.max(Math.abs(dx), Math.abs(dy)) > 2;
      if (g.current.axis === 'x' || (g.current.axis === null && leaningHorizontal)) {
        ev.preventDefault();
      }
    };
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, [isDesktop]);

  if (isDesktop) {
    return <>{children}</>;
  }

  const cancel = () => {
    g.current.id = null;
    g.current.axis = null;
    setDrag(null);
  };

  const onPointerDown = (e) => {
    g.current.pointers.add(e.pointerId);
    // Second finger ⇒ pinch (map zoom). Abandon any swipe and stay out.
    if (g.current.pointers.size >= 2) {
      cancel();
      return;
    }
    const w = (viewportRef.current && viewportRef.current.clientWidth) || window.innerWidth;
    g.current.id = e.pointerId;
    g.current.startX = e.clientX;
    g.current.startY = e.clientY;
    g.current.axis = null;
    g.current.width = w;
    g.current.startT = performance.now();
  };

  const onPointerMove = (e) => {
    if (g.current.id !== e.pointerId || g.current.pointers.size >= 2) return;
    const dx = e.clientX - g.current.startX;
    const dy = e.clientY - g.current.startY;

    if (g.current.axis === null) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_DEADZONE_PX) return;
      // Lock the axis. Vertical ⇒ release to native scroll (Table); done.
      g.current.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (g.current.axis === 'y') {
        g.current.id = null;
        return;
      }
      // NB: do NOT setPointerCapture here. On a touch pointer inside a region
      // whose touch-action still allows scrolling (pan-y on the Table), capture
      // makes the browser fire pointercancel and abort the swipe. The panes are
      // clipped by the overflow-hidden viewport, so the finger never leaves the
      // track and event bubbling already delivers every move to this handler.
    }
    if (g.current.axis === 'x') setDrag(dx);
  };

  const onPointerUp = (e) => {
    g.current.pointers.delete(e.pointerId);
    if (g.current.id !== e.pointerId) return;
    const committedDrag = g.current.axis === 'x' ? drag ?? 0 : 0;
    const w = g.current.width || 1;
    const dt = Math.max(1, performance.now() - g.current.startT);
    const velocity = Math.abs(committedDrag) / dt; // px per ms

    let next = index;
    const crossedThreshold = Math.abs(committedDrag) > w * SNAP_FRACTION;
    const flicked = Math.abs(committedDrag) > FLICK_MIN_PX && velocity > FLICK_MIN_VELOCITY;
    if (crossedThreshold || flicked) {
      next = committedDrag < 0 ? index + 1 : index - 1;
      next = Math.max(0, Math.min(count - 1, next));
    }

    cancel();
    if (next !== index) onChange(tabs[next]);
  };

  // Transform: percentage base (per tab) + live finger delta in px, clamped so
  // you can't drag past either end.
  const basePct = -index * (100 / count);
  let trackStyle;
  if (drag !== null) {
    const w = g.current.width || 1;
    const basePx = -index * w;
    const clamped = Math.max(-(count - 1) * w, Math.min(0, basePx + drag));
    const effDrag = clamped - basePx;
    trackStyle = {
      width: `${count * 100}%`,
      transform: `translateX(calc(${basePct}% + ${effDrag}px))`,
      transition: 'none',
      touchAction: 'pan-y',
    };
  } else {
    trackStyle = {
      width: `${count * 100}%`,
      transform: `translateX(${basePct}%)`,
      transition: 'transform 200ms ease-out',
      touchAction: 'pan-y',
    };
  }

  return (
    <div ref={viewportRef} className="flex-1 overflow-hidden relative">
      <div
        ref={trackRef}
        className="flex h-full will-change-transform"
        style={trackStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        {panes.map((node, i) => (
          <div
            key={tabs[i] ?? i}
            className="shrink-0 h-full overflow-hidden flex"
            style={{ width: `${100 / count}%`, touchAction: 'pan-y' }}
          >
            {node}
          </div>
        ))}
      </div>
    </div>
  );
}
